/**
 * Aparência de cada nível visual (tamanho, opacidade, brilho). Tabela única,
 * usada pelo canvas 2D (atlas de sprites), pelo shader WebGL (gerado a partir
 * destes números) e pela legenda do painel.
 */
import { CLASS_GOES } from './dataset';
import { FLAME_EXTENT } from './flame';
import { FADE_FIRST, FADE_LEVELS, LEVEL_NORMAL } from './timeFilter';

export interface LevelLook {
  /** Fator do raio do núcleo (a chama tem ~1,5× esse raio de altura para cada lado; flame.ts). */
  scale: number;
  alpha: number;
  /** Raio do brilho em múltiplos do raio do núcleo (0 = sem brilho). */
  glow: number;
  glowAlpha: number;
  /** Quanto o núcleo clareia (mistura com branco, 0–1). */
  white: number;
}

interface TrailLook {
  scale: number;
  alpha: number;
  glow: number;
  glowAlphaGoes: number;
  glowAlphaOthers: number;
  white: number;
}

/**
 * Quadro atual e rastro de 2 quadros: o brilho diminui aos poucos até o 1º
 * nível de esmaecimento (núcleo 1,0×, brilho 2,1× α 0,35 no GOES-19).
 */
export const TRAIL_LOOKS: readonly TrailLook[] = [
  { scale: 1.3, alpha: 1, glow: 3.2, glowAlphaGoes: 0.75, glowAlphaOthers: 0.6, white: 0.35 }, // quadro atual
  { scale: 1.2, alpha: 1, glow: 2.9, glowAlphaGoes: 0.6, glowAlphaOthers: 0.42, white: 0.2 }, // 1 quadro antes
  { scale: 1.1, alpha: 0.97, glow: 2.5, glowAlphaGoes: 0.47, glowAlphaOthers: 0.24, white: 0.08 }, // 2 quadros antes
];

/**
 * Esmaecimento: k = 0 (recente) … 1 (mais antigo). O foco antigo se distingue
 * mais pela opacidade que pelo tamanho: até 09/10/2026 ele encolhia 45 % e
 * ficava com ~2 px de raio, pequeno demais para ver e para tocar.
 */
export const FADE = { scaleDrop: 0.25, alpha: 0.95, alphaDrop: 0.6, glowGoes: 2.1, glowAlpha: 0.35 } as const;

/** Aparência de cada nível para cada classe (0 = GOES-19, 1 = outros). */
export function levelLook(cls: number, level: number): LevelLook {
  const goes = cls === CLASS_GOES;
  const trail = TRAIL_LOOKS[level];
  if (level < FADE_FIRST && trail) {
    return {
      scale: trail.scale,
      alpha: trail.alpha,
      glow: trail.glow,
      glowAlpha: goes ? trail.glowAlphaGoes : trail.glowAlphaOthers,
      white: trail.white,
    };
  }
  // Sem brilho nos outros satélites depois do rastro (glowAlpha 0 = sem efeito).
  if (level >= LEVEL_NORMAL) {
    return { scale: 1, alpha: FADE.alpha, glow: goes ? FADE.glowGoes : 0, glowAlpha: goes ? FADE.glowAlpha : 0, white: 0 };
  }
  const k = (level - FADE_FIRST) / Math.max(1, FADE_LEVELS - 1);
  return {
    scale: 1 - FADE.scaleDrop * k,
    alpha: FADE.alpha - FADE.alphaDrop * k,
    glow: goes ? FADE.glowGoes : 0,
    glowAlpha: goes ? FADE.glowAlpha * (1 - k) : 0,
    white: 0,
  };
}

/**
 * Meia extensão de desenho de um nível (brilho ou a própria chama, o que for
 * maior) em múltiplos do raio base.
 */
export const lookExtent = (l: { scale: number; glow: number }) => l.scale * Math.max(l.glow, FLAME_EXTENT);

/** Maior meia extensão de desenho em múltiplos do raio base — para dimensionar sprites. */
export const MAX_EXTENT = Math.max(...TRAIL_LOOKS.map(lookExtent));
